import type { SortOptionConfig } from '../types/movie';

export const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p';

export const IMAGE_SIZES = {
  poster: {
    small: 'w185',
    medium: 'w342',
    large: 'w500',
    original: 'original',
  },
  backdrop: {
    small: 'w300',
    medium: 'w780',
    large: 'w1280',
    original: 'original',
  },
  profile: {
    small: 'w45',
    medium: 'w185',
    large: 'h632',
    original: 'original',
  },
};

// Movie genres
export const GENRES: Record<number, string> = {
  28: 'Action',
  12: 'Adventure',
  16: 'Animation',
  35: 'Comedy',
  80: 'Crime',
  99: 'Documentary',
  18: 'Drama',
  10751: 'Family',
  14: 'Fantasy',
  36: 'History',
  27: 'Horror',
  10402: 'Music',
  9648: 'Mystery',
  10749: 'Romance',
  878: 'Sci-Fi',
  10770: 'TV Movie',
  53: 'Thriller',
  10752: 'War',
  37: 'Western',
};

// Native TV genres that exist in TMDB
export const NATIVE_TV_GENRES: number[] = [
  10759, // Action & Adventure
  16,    // Animation
  35,    // Comedy
  80,    // Crime
  99,    // Documentary
  18,    // Drama
  10751, // Family
  10762, // Kids
  9648,  // Mystery
  10763, // News
  10764, // Reality
  10765, // Sci-Fi & Fantasy
  10766, // Soap
  10767, // Talk
  10768, // War & Politics
  37,    // Western
];

// Keywords to use for movie genres that don't exist as TV genres
export const TV_GENRE_KEYWORD_MAP: Record<number, number[]> = {
  // Horror (movie genre 27) → horror-related keywords
  27: [210024, 315058, 6152, 162846, 12339, 224636],
  // Thriller (movie genre 53) → thriller-related keywords
  53: [10714, 9715, 11322, 207317, 10349],
  // Romance (movie genre 10749) → romance-related keywords
  10749: [9840, 818, 1054, 217067, 284954],
  // History (movie genre 36) → history-related keywords
  36: [9672, 12565, 156174, 10683, 699],
  // Music (movie genre 10402) → music-related keywords
  10402: [5765, 316069, 6038, 9714, 258740],
};

// Movie genre ID to TV genre ID mapping (for genres that have direct equivalents)
export const MOVIE_TO_TV_GENRE_MAP: Record<number, number> = {
  28: 10759,   // Action → Action & Adventure
  12: 10759,   // Adventure → Action & Adventure
  14: 10765,   // Fantasy → Sci-Fi & Fantasy
  878: 10765,  // Sci-Fi → Sci-Fi & Fantasy
  10752: 10768, // War → War & Politics
};

// Unified genre list for the filter UI (shows same genres for both Movies and TV)
export interface UnifiedGenre {
  id: number;           // Movie genre ID (used as the filter value)
  name: string;
  tvId: number | 'keywords';  // TV genre ID or 'keywords' if using keyword workaround
}

export const UNIFIED_GENRES: UnifiedGenre[] = [
  { id: 28, name: 'Action', tvId: 10759 },
  { id: 12, name: 'Adventure', tvId: 10759 },
  { id: 16, name: 'Animation', tvId: 16 },
  { id: 35, name: 'Comedy', tvId: 35 },
  { id: 80, name: 'Crime', tvId: 80 },
  { id: 99, name: 'Documentary', tvId: 99 },
  { id: 18, name: 'Drama', tvId: 18 },
  { id: 10751, name: 'Family', tvId: 10751 },
  { id: 14, name: 'Fantasy', tvId: 10765 },
  { id: 36, name: 'History', tvId: 'keywords' },
  { id: 27, name: 'Horror', tvId: 'keywords' },
  { id: 10402, name: 'Music', tvId: 'keywords' },
  { id: 9648, name: 'Mystery', tvId: 9648 },
  { id: 10749, name: 'Romance', tvId: 'keywords' },
  { id: 878, name: 'Sci-Fi', tvId: 10765 },
  { id: 53, name: 'Thriller', tvId: 'keywords' },
  { id: 10752, name: 'War', tvId: 10768 },
  { id: 37, name: 'Western', tvId: 37 },
];

// TV-only genres (only show when in TV mode)
export const TV_ONLY_GENRES: UnifiedGenre[] = [
  { id: 10762, name: 'Kids', tvId: 10762 },
  { id: 10763, name: 'News', tvId: 10763 },
  { id: 10764, name: 'Reality', tvId: 10764 },
  { id: 10766, name: 'Soap', tvId: 10766 },
  { id: 10767, name: 'Talk', tvId: 10767 },
];

// Combined TV genres for display purposes (native + keyword-mapped)
export const TV_GENRES: Record<number, string> = {
  10759: 'Action & Adventure',
  16: 'Animation',
  35: 'Comedy',
  80: 'Crime',
  99: 'Documentary',
  18: 'Drama',
  10751: 'Family',
  27: 'Horror',
  10762: 'Kids',
  9648: 'Mystery',
  10763: 'News',
  10764: 'Reality',
  10749: 'Romance',
  10765: 'Sci-Fi & Fantasy',
  10766: 'Soap',
  10767: 'Talk',
  53: 'Thriller',
  36: 'History',
  10402: 'Music',
  10768: 'War & Politics',
  37: 'Western',
};

export const LANGUAGES: Record<string, string> = {
  en: 'English',
  fr: 'French',
  es: 'Spanish',
  de: 'German',
  it: 'Italian',
  ja: 'Japanese',
  ko: 'Korean',
  zh: 'Chinese',
  pt: 'Portuguese',
  ru: 'Russian',
  hi: 'Hindi',
  ar: 'Arabic',
  nl: 'Dutch',
  sv: 'Swedish',
  pl: 'Polish',
  tr: 'Turkish',
  th: 'Thai',
  id: 'Indonesian',
  vi: 'Vietnamese',
  da: 'Danish',
  fi: 'Finnish',
  no: 'Norwegian',
  cs: 'Czech',
  el: 'Greek',
  he: 'Hebrew',
  hu: 'Hungarian',
  ro: 'Romanian',
  uk: 'Ukrainian',
};

export const SORT_OPTIONS: SortOptionConfig[] = [
  { value: 'release_date.desc', label: 'Newest' },
  { value: 'release_date.asc', label: 'Oldest' },
  { value: 'runtime.asc', label: 'Runtime - Shortest' },
  { value: 'runtime.desc', label: 'Runtime - Longest' },
  { value: 'our_rating.desc', label: 'Our Rating - Highest' },
  { value: 'our_rating.asc', label: 'Our Rating - Lowest' },
  { value: 'imdb_rating.desc', label: 'IMDb - Highest' },
  { value: 'imdb_rating.asc', label: 'IMDb - Lowest' },
  { value: 'rt_rating.desc', label: 'Rotten Tomatoes - Highest' },
  { value: 'rt_rating.asc', label: 'Rotten Tomatoes - Lowest' },
  { value: 'popularity.desc', label: 'Most Popular' },
  { value: 'popularity.asc', label: 'Least Popular' },
];

export const DEFAULT_FILTERS = {
  genres: [] as number[],
  yearRange: [1920, new Date().getFullYear()] as [number, number],
  runtimeRange: [0, 300] as [number, number],
  ratingRange: [0, 10] as [number, number],
  rtRatingRange: [0, 100] as [number, number],
  tmdbRatingRange: [0, 10] as [number, number],
  ourRatingRange: [0, 10] as [number, number],
  languages: [] as string[],
  actorSearch: '',
  directorSearch: '',
  searchQuery: '',
  sortBy: 'popularity.desc' as const,
};

export const CURRENT_YEAR = new Date().getFullYear();
